// packages/common/async/src/callback.ts
var createPromiseFromCallback = (run) => new Promise((resolve, reject) => {
  run((error, value) => {
    if (error) {
      reject(error);
    } else {
      resolve(value);
    }
  });
});

// packages/common/async/src/chain.ts
var asyncChain = (chain) => async (elements) => {
  let result = await Promise.resolve(elements);
  for (const part of chain) {
    result = await Promise.all(result.map(async (element) => await part(element)));
  }
  return result;
};

// packages/common/async/src/debounce.ts
var debounce = (cb, wait = 100) => {
  let t;
  return (arg) => {
    clearTimeout(t);
    t = setTimeout(() => cb(arg), wait);
  };
};

// packages/common/async/src/errors.ts
var toError = (err) => err === void 0 || typeof err === "string" ? new Error(err) : err;
var TimeoutError = class extends Error {
  constructor(timeout, label) {
    super(timeout ? `Timeout [${timeout}ms]${label === void 0 ? "" : `: ${label}`}` : "Timeout");
  }
};
var observableError = (observable, err) => {
  var _a, _b;
  if (err instanceof TimeoutError) {
    (_b = (_a = observable.callback).onTimeout) == null ? void 0 : _b.call(_a, err);
  } else {
    observable.callback.onError(toError(err));
  }
};

// packages/common/async/src/timeout.ts
var sleep = (ms) => {
  return new Promise((resolve) => {
    const finish = Date.now() + ms;
    const sleeper = () => {
      const delta = finish - Date.now();
      if (delta > 0) {
        setTimeout(sleeper, delta);
      } else {
        resolve();
      }
    };
    sleeper();
  });
};
var asyncTimeout = (promise, timeout, err) => {
  const throwable = err === void 0 || typeof err === "string" ? new TimeoutError(timeout, err) : err;
  const conditionTimeout = typeof promise === "function" ? createPromiseFromCallback(promise) : promise;
  let timeoutId;
  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(throwable);
    }, timeout);
    if (typeof timeoutId === "object" && "unref" in timeoutId) {
      timeoutId.unref();
    }
  });
  return Promise.race([
    conditionTimeout,
    timeoutPromise
  ]).finally(() => {
    clearTimeout(timeoutId);
  });
};

// packages/common/async/src/event-emitter.ts
var onEvent = (eventEmitter, eventName, callback) => {
  eventEmitter.on(eventName, callback);
  return () => eventEmitter.off(eventName, callback);
};
var addListener = (eventEmitter, eventName, callback) => {
  const off = onEvent(eventEmitter, eventName, callback);
  return {
    remove: () => off()
  };
};
var waitForEvent = (eventEmitter, eventName, test, timeout, error) => {
  let off;
  const promise = new Promise((resolve) => {
    off = onEvent(eventEmitter, eventName, (...args) => {
      if (!test || test(...args)) {
        resolve(...args);
      }
    });
  });
  return timeout ? asyncTimeout(promise, timeout, error != null ? error : new Error()).finally(off) : promise.finally(off);
};

// packages/common/async/src/events.ts
import { Context } from "@dxos/context";

// packages/common/async/src/task-scheduling.ts
import { StackTrace as StackTrace2 } from "@dxos/debug";

// packages/common/async/src/track-leaks.ts
import { StackTrace } from "@dxos/debug";
var enabled = typeof process !== "undefined" && !!process.env.DX_TRACK_LEAKS;
var openResources = /* @__PURE__ */ new Set();
var handleSymbol = Symbol("checkLeaksHandle");
var trackResource = (resource) => {
  if (!enabled) {
    return () => {
    };
  }
  openResources.add(resource);
  return () => {
    openResources.delete(resource);
  };
};
var trackLeaks = (open, close) => (target) => {
  if (!enabled) {
    return;
  }
  const openMethod = target.prototype[open];
  const closeMethod = target.prototype[close];
  if (!openMethod || !closeMethod) {
    throw new Error(`Cannot find ${open} or ${close} method in ${target.name}`);
  }
  {
    target.prototype[open] = async function(...args) {
      this[handleSymbol] = trackResource({
        name: target.name,
        openStack: new StackTrace()
      });
      return openMethod.apply(this, args);
    };
    Object.defineProperty(target.prototype[open], "name", {
      value: open + "$checkLeaks"
    });
  }
  {
    target.prototype[close] = async function(...args) {
      var _a;
      (_a = this[handleSymbol]) == null ? void 0 : _a.call(this);
      return closeMethod.apply(this, args);
    };
    Object.defineProperty(target.prototype[close], "name", {
      value: close + "$checkLeaks"
    });
  }
};
var dumpLeaks = () => {
  if (!enabled) {
    return;
  }
  console.log(`Leaked resources ${openResources.size}:`);
  for (const resource of openResources) {
    console.log(` - ${resource.name} at`);
    console.log(resource.openStack.getStack());
    console.log();
  }
};
if (enabled) {
  global.dxDumpLeaks = dumpLeaks;
}

// packages/common/async/src/task-scheduling.ts
var DeferredTask = class {
  constructor(_ctx, _callback) {
    this._ctx = _ctx;
    this._callback = _callback;
    this._scheduled = false;
    this._promise = null;
  }
  schedule() {
    if (this._scheduled) {
      return;
    }
    scheduleTask(this._ctx, async () => {
      await this._promise;
      this._scheduled = false;
      this._promise = runInContextAsync(this._ctx, () => this._callback());
    });
    this._scheduled = true;
  }
};
var runInContext = (ctx, fn) => {
  try {
    fn();
  } catch (err) {
    ctx.raise(err);
  }
};
var runInContextAsync = async (ctx, fn) => {
  try {
    await fn();
  } catch (err) {
    ctx.raise(err);
  }
};
var scheduleTask = (ctx, fn, afterMs) => {
  const clearTracking = trackResource({
    name: `task (${fn.name || "anonymous"})`,
    openStack: new StackTrace2()
  });
  const timeout = setTimeout(async () => {
    await runInContextAsync(ctx, fn);
    clearTracking();
  }, afterMs);
  ctx.onDispose(() => {
    clearTracking();
    clearTimeout(timeout);
  });
};
var scheduleTaskInterval = (ctx, task, interval) => {
  const clearTracking = trackResource({
    name: `repeating task (${task.name || "anonymous"})`,
    openStack: new StackTrace2()
  });
  let timeoutId;
  const run = async () => {
    await runInContextAsync(ctx, task);
    timeoutId = setTimeout(run, interval);
  };
  timeoutId = setTimeout(run, interval);
  ctx.onDispose(() => {
    clearTracking();
    clearTimeout(timeoutId);
  });
};

// packages/common/async/src/events.ts
var EventSubscriptions = class {
  constructor() {
    this._listeners = [];
  }
  add(cb) {
    this._listeners.push(cb);
  }
  clear() {
    this._listeners.forEach((cb) => cb());
    this._listeners.length = 0;
  }
};
var Event = class {
  constructor() {
    this._listeners = /* @__PURE__ */ new Map();
    this._onceListeners = /* @__PURE__ */ new Map();
    this._effects = /* @__PURE__ */ new Set();
  }
  static wrap(emitter, eventName) {
    const event = new Event();
    event.addEffect(() => {
      const onEvent2 = (data) => event.emit(data);
      emitter.on(eventName, onEvent2);
      return () => emitter.off(eventName, onEvent2);
    });
    return event;
  }
  /**
  * Emit an event.
  * In most cases should only be called by the class or entity containing the event.
  * All listeners are called in order of subscription with persistent ones first.
  * Listeners are called synchronously in the same stack.
  * A thrown exception in the listener will stop the event from being emitted to the rest of the listeners.
  *
  * @param data param that will be passed to all listeners.
  */
  emit(data) {
    for (const [_key, listener] of this._listeners) {
      listener(data);
    }
    for (const [_key1, listener1] of this._onceListeners) {
      listener1(data);
      this._onceListeners.delete(_key1);
    }
  }
  on(_ctx, _callback) {
    const [ctx, callback] = _ctx instanceof Context ? [
      _ctx,
      _callback
    ] : [
      new Context(),
      _ctx
    ];
    const runCallback = (data) => runInContextAsync(ctx, () => callback(data));
    this._listeners.set(callback, runCallback);
    if (this.listenerCount() === 1) {
      this._runEffects();
    }
    ctx.onDispose(() => this.off(callback));
    return () => this.off(callback);
  }
  /**
  * Unsubscribe this callback from new events. Inncludes persistent and once-listeners.
  * NOTE: It is recommended to use `Event.on`'s return value instead.
  * If the callback is not subscribed this is no-op.
  *
  * @param callback
  */
  off(callback) {
    this._listeners.delete(callback);
    this._onceListeners.delete(callback);
    if (this.listenerCount() === 0) {
      this._cleanupEffects();
    }
  }
  once(_ctx, _callback) {
    const [ctx, callback] = _ctx instanceof Context ? [
      _ctx,
      _callback
    ] : [
      new Context(),
      _ctx
    ];
    if (this._listeners.has(callback)) {
      return () => {
      };
    }
    const runCallback = (data) => runInContextAsync(ctx, () => callback(data));
    this._onceListeners.set(callback, runCallback);
    if (this.listenerCount() === 1) {
      this._runEffects();
    }
    return () => {
      this._onceListeners.delete(callback);
      if (this.listenerCount() === 0) {
        this._runEffects();
      }
    };
  }
  /**
  * An async iterator that iterates over events.
  * This iterator runs indefinitely.
  */
  async *[Symbol.asyncIterator]() {
    while (true) {
      yield await new Promise((resolve) => {
        this.once(resolve);
      });
    }
  }
  /**
  * Returns a promise that resolves with the first event emitted that matches the provided predicate.
  *
  * @param predicate
  */
  waitFor(predicate) {
    return new Promise((resolve) => {
      const unsubscribe = this.on((data) => {
        if (predicate(data)) {
          unsubscribe();
          resolve(data);
        }
      });
    });
  }
  /**
  * Returns a promise that resolves once a specific number of events was emitted since this method was called.
  *
  * @param expectedCount
  */
  waitForCount(expectedCount) {
    let count = 0;
    return this.waitFor(() => ++count === expectedCount);
  }
  /**
  * Similar to waitFor, but the promise resolves immediately if the condition is already true.
  */
  // TODO(burdon): Should pass event property to predicate.
  async waitForCondition(predicate) {
    if (!predicate()) {
      await this.waitFor(predicate);
    }
  }
  /**
  * Returns the number of persistent listeners.
  */
  listenerCount() {
    return this._listeners.size + this._onceListeners.size;
  }
  /**
  * Add a side effect that will be activated once the event has at least one subscriber.
  * The provided callback can return a function that will be used to clean up after the last subscriber unsubscribes from the event.
  * The API is similar to `useEffect` from React.
  *
  * ## Example:
  * ```typescript
  * event.addEffect(() => {
  *   // do stuff
  *   return () => {
  *     // clean-up
  *   };
  * });
  * ```
  *
  * @returns Callback that will remove this effect once called.
  */
  addEffect(effect) {
    const handle = {
      effect,
      cleanup: void 0
    };
    if (this.listenerCount() > 0) {
      handle.cleanup = handle.effect();
    }
    this._effects.add(handle);
    return () => {
      var _a;
      (_a = handle.cleanup) == null ? void 0 : _a.call(handle);
      this._effects.delete(handle);
    };
  }
  /**
  * Triggers an event with at least `timeout` milliseconds between each event.
  * If the event is triggered more often, the event is delayed until the timeout is reached.
  * If event is emitted for the first time or event wasn't fired for `timeout` milliseconds, the event is emitted after `timeout / 8` ms.
  */
  debounce(timeout = 0) {
    const debouncedEvent = new Event();
    let firing;
    let lastFired;
    debouncedEvent.addEffect(() => {
      const unsubscribe = this.on(() => {
        if (!firing) {
          const fireIn = !lastFired || lastFired > timeout ? timeout / 8 : timeout;
          firing = setTimeout(() => {
            lastFired = Date.now();
            firing = void 0;
            debouncedEvent.emit();
          }, fireIn);
        }
      });
      return () => {
        unsubscribe();
        clearTimeout(firing);
      };
    });
    return debouncedEvent;
  }
  /**
  * Turn any variant of `Event<T>` into an `Event<void>` discarding the callback parameter.
  */
  discardParameter() {
    return this;
  }
  /**
  * Overridden to not return implementation details.
  */
  toJSON() {
    return {
      listenerCount: this.listenerCount()
    };
  }
  _runEffects() {
    for (const handle of this._effects) {
      handle.cleanup = handle.effect();
    }
  }
  _cleanupEffects() {
    var _a;
    for (const handle of this._effects) {
      (_a = handle.cleanup) == null ? void 0 : _a.call(handle);
      handle.cleanup = void 0;
    }
  }
};

// packages/common/async/src/latch.ts
import assert from "@dxos/node-std/assert";
var latch = ({ count = 1, timeout } = {}) => {
  assert(count >= 0);
  let t;
  let doResolve;
  let doReject;
  const promise = new Promise((resolve, reject) => {
    doResolve = (value) => {
      clearTimeout(t);
      resolve(value);
    };
    doReject = (err) => {
      clearTimeout(t);
      reject(err);
    };
  });
  if (count === 0) {
    setTimeout(() => {
      doResolve(0);
    });
  } else {
    if (timeout) {
      t = setTimeout(() => {
        doReject(new Error(`Timed out after ${timeout}ms`));
      }, timeout);
    }
  }
  let i = 0;
  return [
    async () => await promise,
    () => {
      if (++i === count) {
        doResolve(i);
      }
      return i;
    },
    (err) => doReject(err)
  ];
};

// packages/common/async/src/observable.ts
import Observable from "zen-observable";
import PushStream from "zen-push";
var MulticastObservable = class extends Observable {
  constructor(subscriber, _value) {
    super((observer) => this._subscribe(observer));
    this._value = _value;
    this._observers = /* @__PURE__ */ new Set();
    this._handlers = {
      next: (value) => {
        this._value = value;
        this._observers.forEach((observer) => {
          var _a;
          return (_a = observer.next) == null ? void 0 : _a.call(observer, value);
        });
      },
      error: (err) => {
        this._observers.forEach((observer) => {
          var _a;
          return (_a = observer.error) == null ? void 0 : _a.call(observer, err);
        });
      },
      complete: () => {
        this._observers.forEach((observer) => {
          var _a;
          return (_a = observer.complete) == null ? void 0 : _a.call(observer);
        });
      }
    };
    this._observerable = typeof subscriber === "function" ? new Observable(subscriber) : subscriber;
    this._subscription = this._observerable.subscribe(this._handlers);
  }
  static from(value, initialValue) {
    if ("emit" in value) {
      return new MulticastObservable((observer) => {
        value.on((data) => {
          observer.next(data);
        });
      }, initialValue);
    }
    const observable = super.from(value);
    return new MulticastObservable(observable, initialValue);
  }
  static of(...items) {
    return new MulticastObservable(super.of(...items.slice(1)), items[0]);
  }
  /**
  * @returns Stable reference to an observable that always returns `undefined`.
  */
  static empty() {
    return EMPTY_OBSERVABLE;
  }
  /**
  * Get the current value of the observable.
  */
  get() {
    if (this._value === void 0) {
      throw new Error("MulticastObservable is not initialized.");
    }
    return this._value;
  }
  _subscribe(observer) {
    var _a;
    if (this._subscription.closed) {
      this._subscription = this._observerable.subscribe(this._handlers);
    }
    if (!this._observers.has(observer) && this._value !== void 0) {
      (_a = observer.next) == null ? void 0 : _a.call(observer, this._value);
      this._observers.add(observer);
    }
    return () => {
      this._observers.delete(observer);
      if (this._observers.size === 0) {
        this._subscription.unsubscribe();
      }
    };
  }
};
var EMPTY_OBSERVABLE = MulticastObservable.of(null);

// packages/common/async/src/observable-value.ts
import { createSetDispatch } from "@dxos/util";
var ObservableProvider = class {
  constructor() {
    this._handlers = /* @__PURE__ */ new Set();
    this._proxy = createSetDispatch({
      handlers: this._handlers
    });
  }
  /**
  * Proxy used to dispatch callbacks to each subscription.
  */
  get callback() {
    return this._proxy;
  }
  get value() {
    return this._value;
  }
  setValue(value) {
    this._value = value;
  }
  subscribe(handler) {
    this._handlers.add(handler);
    return () => {
      this._handlers.delete(handler);
    };
  }
};
var CancellableObservableProvider = class extends ObservableProvider {
  // prettier-ignore
  constructor(_handleCancel) {
    super();
    this._handleCancel = _handleCancel;
    this._cancelled = false;
  }
  get cancelled() {
    return this._cancelled;
  }
  async cancel() {
    var _a, _b, _c;
    if (this._cancelled) {
      return;
    }
    this._cancelled = true;
    await ((_a = this._handleCancel) == null ? void 0 : _a.call(this));
    (_c = (_b = this.callback).onCancelled) == null ? void 0 : _c.call(_b);
  }
};

// packages/common/async/src/lock.ts
import { warnAfterTimeout } from "@dxos/debug";
var Lock = class {
  constructor() {
    this._queue = Promise.resolve();
    this._tag = null;
  }
  get tag() {
    return this._tag;
  }
  /**
  * Acquires the lock.
  * Caller is responsible for releasing the lock using the returned callback.
  * NOTE: Using `executeSynchronized` is preferred over using `acquire` directly.
  * @returns Release callback
  */
  async acquire(tag) {
    const prev = this._queue;
    let release;
    this._queue = new Promise((resolve) => {
      release = () => {
        this._tag = null;
        resolve();
      };
    });
    await prev;
    if (tag !== void 0) {
      this._tag = tag;
    }
    return release;
  }
  /**
  * Waits for all previous executions to complete and then executes a given function.
  * Only a single function can be executed at a time.
  * Function are executed in the same order as `executeSynchronized` is called.
  * WARNING: Calling `executeSynchronized` inside of `executeSynchronized` on the same lock instance is a deadlock.
  */
  async executeSynchronized(fun) {
    const release = await this.acquire();
    try {
      return await fun();
    } finally {
      release();
    }
  }
};
var classLockSymbol = Symbol("class-lock");
var synchronized = (target, propertyName, descriptor) => {
  const method = descriptor.value;
  descriptor.value = async function(...args) {
    var _a;
    const lock = (_a = this[classLockSymbol]) != null ? _a : this[classLockSymbol] = new Lock();
    const tag = `${target.constructor.name}.${propertyName}`;
    const release = await warnAfterTimeout(500, `lock on ${tag} (taken by ${lock.tag})`, () => lock.acquire(tag));
    try {
      return await method.apply(this, args);
    } finally {
      release();
    }
  };
  Object.defineProperty(descriptor.value, "name", {
    value: propertyName + "$synchronized"
  });
};

// packages/common/async/src/trigger.ts
var trigger = (timeout) => {
  let callback;
  const promise = new Promise((resolve, reject) => {
    if (timeout) {
      setTimeout(() => reject(new Error(`Timed out after ${timeout}ms`)), timeout);
    }
    callback = resolve;
  });
  const provider = () => promise;
  const resolver = (value) => callback(value);
  return [
    provider,
    resolver
  ];
};
var Trigger = class {
  constructor(_options = {
    autoReset: false
  }) {
    this._options = _options;
    this.reset();
  }
  /**
  * Wait until wake is called, with optional timeout.
  */
  async wait({ timeout } = {}) {
    if (timeout) {
      return asyncTimeout(this._promise, timeout, new TimeoutError(timeout));
    } else {
      return this._promise;
    }
  }
  /**
  * Wake blocked callers (if any).
  */
  wake(value) {
    this._wake(value);
    if (this._options.autoReset) {
      return this.reset();
    }
    return this;
  }
  /**
  * Reset promise (new waiters will wait).
  */
  reset() {
    this._promise = new Promise((resolve) => {
      this._wake = resolve;
    });
    return this;
  }
  /**
  * Throw error to blocked callers (if any).
  */
  throw(error) {
    this._wake(Promise.reject(error));
    if (this._options.autoReset) {
      return this.reset();
    }
    return this;
  }
};

// packages/common/async/src/sink.ts
var sink = (emitter, event, count = 1) => {
  const [getPromise, resolve] = trigger();
  let counter = 0;
  const listener = () => {
    if (++counter === count) {
      emitter.off(event, listener);
      resolve();
    }
  };
  emitter.on(event, listener);
  return getPromise();
};

// packages/common/async/src/testing.ts
var waitForCondition = (condition, timeout = 0, interval = 10) => {
  const stopTime = timeout ? Date.now() + timeout : 0;
  const [provider, resolver] = trigger();
  const waiter = async () => {
    while (!stopTime || Date.now() < stopTime) {
      try {
        const value = await condition();
        if (value) {
          resolver(value);
          break;
        }
      } catch (err) {
      }
      await sleep(interval);
    }
  };
  setTimeout(waiter, 0);
  return timeout ? asyncTimeout(provider(), timeout, new Error("Timeout")) : provider();
};

// packages/common/async/src/stream-to-array.ts
var streamToArray = (stream) => {
  let deferred;
  if (!stream.readable) {
    deferred = Promise.resolve([]);
  } else {
    deferred = new Promise((resolve, reject) => {
      if (!stream.readable) {
        return resolve([]);
      }
      let arr = [];
      const onData = (doc) => {
        arr == null ? void 0 : arr.push(doc);
      };
      const onEnd = (err) => {
        if (err) {
          reject(err);
        } else {
          resolve(arr);
        }
        cleanup();
      };
      const onClose = () => {
        resolve(arr);
        cleanup();
      };
      const cleanup = () => {
        arr = [];
        stream.removeListener("data", onData);
        stream.removeListener("end", onEnd);
        stream.removeListener("error", onEnd);
        stream.removeListener("close", onClose);
      };
      stream.on("data", onData);
      stream.on("end", onEnd);
      stream.on("error", onEnd);
      stream.on("close", onClose);
    });
  }
  return deferred;
};

// packages/common/async/src/until.ts
var until = (cb, timeout) => {
  return new Promise((resolve, reject) => {
    const t = timeout && setTimeout(() => {
      reject(new Error(`Timeout after ${t}ms`));
    }, timeout);
    setTimeout(async () => {
      try {
        await cb((value) => {
          t && clearTimeout(t);
          resolve(value);
        }, (error) => {
          t && clearTimeout(t);
          reject(error);
        });
      } catch (err) {
        reject(err);
      }
    });
  });
};
var untilPromise = (cb) => cb();
var untilError = (cb) => {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        await cb();
        reject(new Error("No error was thrown."));
      } catch (err) {
        resolve(err);
      }
    });
  });
};

// packages/common/async/src/test-stream.ts
import { Duplex } from "@dxos/node-std/stream";
var TestStream = class extends Duplex {
  constructor() {
    super(...arguments);
    this._received = Buffer.alloc(0);
    this._onWrite = new Event();
  }
  static async assertConnectivity(stream1, stream2, { timeout = 200 } = {}) {
    stream1.push("ping");
    stream2.push("pong");
    await Promise.all([
      stream2.assertReceivedAsync("ping", {
        timeout
      }),
      stream1.assertReceivedAsync("pong", {
        timeout
      })
    ]);
  }
  _write(chunk, encoding, callback) {
    this._received = Buffer.concat([
      this._received,
      chunk
    ]);
    this._onWrite.emit();
    callback();
  }
  _read(size) {
  }
  assertReceivedAsync(data, { timeout = 200 } = {}) {
    const dataBuffer = Buffer.isBuffer(data) ? data : Buffer.from(data);
    return asyncTimeout(this._onWrite.waitForCondition(() => this._received.equals(dataBuffer)), timeout);
  }
};
export {
  CancellableObservableProvider,
  DeferredTask,
  Event,
  EventSubscriptions,
  Lock,
  MulticastObservable,
  Observable,
  ObservableProvider,
  PushStream,
  TestStream,
  TimeoutError,
  Trigger,
  addListener,
  asyncChain,
  asyncTimeout,
  createPromiseFromCallback,
  debounce,
  dumpLeaks,
  latch,
  observableError,
  onEvent,
  runInContext,
  runInContextAsync,
  scheduleTask,
  scheduleTaskInterval,
  sink,
  sleep,
  streamToArray,
  synchronized,
  toError,
  trackLeaks,
  trackResource,
  trigger,
  until,
  untilError,
  untilPromise,
  waitForCondition,
  waitForEvent
};
//# sourceMappingURL=index.mjs.map
